import { useGoogleMap } from "@/GoogleMapContext";
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from "@mui/material";
import { GoogleMap, InfoWindow, Marker } from "@react-google-maps/api";
import { memo, useState } from "react";

interface ViewLocationProps {
  modalOpen: boolean;
  closeModal: () => void;
  title: string;
  lat: string;
  lng: string;
  width?: string;
}

const mapContainerStyle = {
  width: "100%",
  height: "50vh",
};

const ViewLocation: React.FC<ViewLocationProps> = ({ modalOpen, closeModal, title, lat, lng, width }) => {
  const [address, setAddress] = useState<string>("");
  const [showInfo, setShowInfo] = useState<boolean>(false);

  const { isLoaded, loadError } = useGoogleMap();

  if (loadError) return <p>Map failed to load</p>;
  if (!isLoaded) return <p>Loading map...</p>;

  if (!modalOpen || !lat || !lng) {
    return null;
  }

  const parsedLat = Number(lat);
  const parsedLng = Number(lng);

  if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
    return null;
  }

  const mapCenter = { lat: parsedLat, lng: parsedLng };

  const getAddress = () => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: mapCenter }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        setAddress(results[0].formatted_address);
        setShowInfo(true);
      }
    });
  };

  const hideGoogleCloseButton = () => {
    const closeButton = document.querySelector(".gm-ui-hover-effect") as HTMLElement;
    if (closeButton) closeButton.style.display = "none";
  };

  const handleClose = () => {
    setShowInfo(false);
    closeModal();
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          style: { width: width || "500px" },
        }
      }}>
      <DialogTitle variant="h5">{title}</DialogTitle>

      <DialogContent dividers>
        <GoogleMap
          center={mapCenter}
          zoom={15}
          mapContainerStyle={mapContainerStyle}
        >
          {/* Marker */}
          <Marker
            position={mapCenter}
            onClick={getAddress}
          />
          {/* Show Address on marker clicked */}
          {showInfo && (
            <InfoWindow
              position={mapCenter}
              onDomReady={hideGoogleCloseButton}
              options={{
                maxWidth: 200,
                pixelOffset: new window.google.maps.Size(0, -40),
                disableAutoPan: true,
              }}
            >
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 6,
                alignItems: "center",
                fontSize: "1.1em",
              }}>
                <small>
                  <strong>Address: </strong>{address}
                </small>
                <button
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#555",
                    fontWeight: "bold",
                  }}
                  onClick={() => setShowInfo(false)}
                >
                  ×
                </button>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </DialogContent>

      <DialogActions>
        <Button
          style={{ color: "black" }}
          onClick={handleClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(ViewLocation);
